import {FormControl, Grid, InputAdornment, InputLabel, MenuItem, Switch, TextField, Typography} from "@mui/material";
import * as React from "react";
import {Field, Form, Formik} from "formik";
import {Add, Delete} from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import {useEffect} from "react";
import {translate} from "../../../translate/translate";
import {Project} from "../../../models/basic/project";
import {ProjectService} from "../../../services/project-service";
import Select from "@mui/material/Select";
import {transformCurrency, transformNumber} from "../../../utils/format-number";
import {toast} from "react-toastify";

const AddProjectAmortizationComponent = (props: any) => {
    const {
        changeList,
        invoiceValue,
        id_client,
    } = props;

    const service = new ProjectService();
    const [project, setProject] = React.useState<number>();
    const [value, setValue] = React.useState<number>(0);
    const [inputValue, setInputValue] = React.useState<string>('R$ 0,00');
    const [listControl, setListControl] = React.useState<any[]>([]);
    const [projectsList, setProjectsList] = React.useState<Project[]>([]);
    const [checked, setChecked] = React.useState<boolean>(false);

    useEffect(() => {
        changeList(listControl);
    }, []);

    useEffect(() => {
        getProjects();
    }, [id_client]);

    useEffect(() => {
        if (invoiceValue) {
            setAllValuesAsInvoiceValue(checked);
            alertSumOfValuesGreater();
        } else {
            setChecked(false);
        }
    }, [invoiceValue, listControl.length]);

    const getProjects = async () => {
        const projects = await service.readAll({"client": id_client});
        setProjectsList(projects.data);
    }

    const addToList = () => {
        const newItem = { project: project, value: value, formatted_value: inputValue };
        const updatedList = [newItem, ...listControl];
        setListControl(updatedList);
        changeList(updatedList);
    };

    const removeFromList = (index: number) => {
        const updatedList = listControl.filter((_: any, i: number) => i !== index);
        setListControl(updatedList);
        changeList(updatedList);
    };

    const changeValueFromList = (value: any, index: number, field: string) => {
        const updatedList = [...listControl];
        if (field === "value") {
            updatedList[index]["formatted_value"] = transformCurrency(transformNumber(value));
            updatedList[index][field] = transformNumber(value);
            setListControl(updatedList);
            changeList(updatedList);
            alertSumOfValuesGreater();
            return
        }
        updatedList[index][field] = value;
        setListControl(updatedList);
        changeList(updatedList);
    };

    const handleChange = (event: any) => {
        let rawValue = event.target.value;
        let numericValue = transformNumber(rawValue);
        setInputValue(transformCurrency(numericValue));
        setValue(numericValue);
    };

    const clearInputs = () => {
        setProject(undefined);
        !checked && setValue(0);
        !checked && setInputValue("");
    }

    const handleSwitch = (event: any) => {
        setChecked(event.target.checked);
        setAllValuesAsInvoiceValue(event.target.checked);
    }

    const setAllValuesAsInvoiceValue = (checked: boolean) => {
        if (checked) {
            const dividedValue = listControl.length ? invoiceValue / listControl.length : invoiceValue;
            const updatedList = listControl.map((item: any) => {
                return { ...item, value: dividedValue, formatted_value: transformCurrency(dividedValue) };
            });
            setListControl(updatedList);
            changeList(updatedList);
            setValue(dividedValue);
        }
    }

    const alertSumOfValuesGreater = () => {
        if (listControl.length && !checked) {
            const sum = listControl.reduce((acc: number, item: any) => acc + item.value, 0);
            if (sum > invoiceValue) {
                toast.warning(translate.t("sum_of_values_greater_than_invoice_value"));
                return true;
            }
        }
    }

    return (
        <>
            <Grid container spacing={2} direction={"row"}>
                <Grid size={6}
                      sx={{
                          display: "flex",
                          alignItems: "center"
                      }}
                >
                    <Typography variant={"h6"}>{translate.t("projects_for_amortization")}</Typography>
                </Grid>
                <Grid size={6}
                      sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "center"
                      }}
                >
                    <Switch
                        color="secondary"
                        name="switch_value"
                        checked={checked}
                        onChange={(e: any) => {
                            handleSwitch(e)
                        }}
                        disabled={!invoiceValue}
                    />
                    <Typography variant={"body2"}>{translate.t("equal_value_for_all_projects")}.</Typography>
                </Grid>
            </Grid>
            <Grid p={0} mt={1} gap={2}>
                <Formik
                    enableReinitialize={true}
                    validateOnMount={true}
                    initialValues={{ project: "", amortization_value: "" }}
                    onSubmit={(values, { setSubmitting }) => {}}
                >
                    {() => (
                        <Form autoComplete="off">
                            <Grid alignItems="center" container spacing={2} direction={"row"} mb={1}>
                                <Grid size={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>
                                            {translate.t("project")}
                                        </InputLabel>
                                        <Select
                                            name="project"
                                            label={translate.t("project")}
                                            variant={"outlined"}
                                            fullWidth
                                            autoFocus={false}
                                            value={project || ""}
                                            disabled={!id_client}
                                            MenuProps={{
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: '400px',
                                                    },
                                                },
                                            }}
                                            onChange={(event) => setProject(Number(event.target.value))}
                                        >
                                            {projectsList?.map((p: Project) => (
                                                <MenuItem key={p.id} value={p.id}>
                                                    {p.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid size={4}>
                                    <Field
                                        as={TextField}
                                        name="amortization_value"
                                        label={translate.t("amortization_value")}
                                        variant={"outlined"}
                                        required={!checked}
                                        fullWidth
                                        autoFocus={true}
                                        value={inputValue}
                                        onChange={handleChange}
                                        disabled={checked}
                                    />
                                </Grid>
                                <Grid size={2}>
                                    <IconButton
                                        id="btn_menu"
                                        color="inherit"
                                        aria-label="add"
                                        disabled={!project || (!checked && !value)}
                                        edge="start"
                                        onClick={() => {
                                            addToList();
                                            clearInputs();
                                        }}
                                    >
                                        <Add />
                                    </IconButton>
                                </Grid>
                            </Grid>
                            {listControl.length > 0 && (
                                <>
                                    {listControl.map((item: any, index: number) => (
                                        <Grid
                                            key={index}
                                            alignItems="center"
                                            container
                                            spacing={2}
                                            mt={1}
                                            direction={"row"}
                                        >
                                            <Grid size={6}>
                                                <FormControl fullWidth>
                                                    <InputLabel>
                                                        {translate.t("project")}
                                                    </InputLabel>
                                                    <Select
                                                        name={`project-${index}`}
                                                        label={translate.t("project")}
                                                        variant={"outlined"}
                                                        fullWidth
                                                        autoFocus={false}
                                                        value={item["project"]}
                                                        onChange={(event) => {
                                                            changeValueFromList(Number(event.target.value), index, "project")
                                                        }}
                                                        error={!item["project"]}
                                                    >
                                                        {projectsList?.map((p: Project) => (
                                                            <MenuItem key={p.id} value={p.id}>
                                                                {p.name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid size={4}>
                                                <Field
                                                    as={TextField}
                                                    name={`value-${index}`}
                                                    label={translate.t("amortization_value")}
                                                    fullWidth
                                                    required={!checked}
                                                    disabled={checked}
                                                    variant={"outlined"}
                                                    autoFocus={false}
                                                    value={item["formatted_value"]}
                                                    onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                                                        changeValueFromList(event.target.value, index, "value")
                                                    }}
                                                    error={!checked && !item["value"]}
                                                />
                                            </Grid>
                                            <Grid size={2}>
                                                <IconButton
                                                    id="btn_menu"
                                                    color="inherit"
                                                    aria-label="remove"
                                                    onClick={() => removeFromList(index)}
                                                    edge="start"
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                    ))}
                                </>
                            )}
                        </Form>
                    )}
                </Formik>
            </Grid>
        </>
    );
};

export default AddProjectAmortizationComponent;

